import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { TriStateRange } from '~/_shared/constants/triStateRange.enum';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useTranslation } from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { activeBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { openMapTool } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { useIsDataLayerToolActiveSelector } from '~/store/frontendState/mapTools/dataLayers/dataLayers.selectors';
import { mapSettingsDataLayersRemoveDataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import {
  useMapSettingsBoundaryGroupsPerDataLayersSelector, useMapSettingsDataLayerGroupsSelector,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.selectors';
import { isBoundaryGroupHidden } from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.state';
import { setDataLayersFilter } from '~/store/mapSettings/toolsState/dataLayers/mapSettingsToolsStateDataLayers.actionCreators';
import { getDataLayersFilterKey } from '~/store/mapSettings/toolsState/dataLayers/mapSettingsToolsStateDataLayers.helpers';
import { useDataLayersFilters } from '~/store/mapSettings/toolsState/dataLayers/mapSettingsToolsStateDataLayers.selectors';
import { createMapStatusBarItemFromDataLayer } from '../item/mapStatusBarItem.factory';
import type { MapStatusBarItem } from '../mapStatusBarItem.type';

export const useDataLayersStatusBarItems = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const dataLayerInstances = useMapSettingsDataLayerGroupsSelector();
  const boundaryFilters = useDataLayersFilters();
  const boundaryGroupPerDataLayer = useMapSettingsBoundaryGroupsPerDataLayersSelector();
  const isDataLayersToolActive = useIsDataLayerToolActiveSelector();
  const activeBoundaryTerritoryGroups = useSelector(state => activeBoundaryTerritoryGroupsSelector(state, true));

  return useMemo<MapStatusBarItem[]>(() => {
    return dataLayerInstances.map(dataLayer => {
      const boundaryGroupId = boundaryGroupPerDataLayer[dataLayer.id];
      const filter = boundaryGroupId ? boundaryFilters.get(boundaryGroupId) : undefined;
      const isAnyBoundaryVisible = !filter || !isBoundaryGroupHidden(filter); // if filter not found, visible

      return createMapStatusBarItemFromDataLayer({
        dataLayer,
        isVisible: isDataLayersToolActive && isAnyBoundaryVisible,
        t,
        onRemove: () => dispatch(mapSettingsDataLayersRemoveDataLayer(dataLayer)),
        onClick: () => {
          dispatch(openMapTool(MapTool.DataLayers));
        },
        onVisibilityChange: (isVisible: boolean) => {
          if (isVisible && !isDataLayersToolActive) {
            dispatch(openMapTool(MapTool.DataLayers));

            if (isAnyBoundaryVisible) {
              return; // the item only looked hidden because the tool was not active, so the filter must stay as it is
            }
          }

          const activeBoundaryTerritoryGroup = activeBoundaryTerritoryGroups.find(btg => btg.boundaryGroupId === boundaryGroupId);
          if (activeBoundaryTerritoryGroup) {
            dispatch(setDataLayersFilter(getDataLayersFilterKey(activeBoundaryTerritoryGroup), {
              ...filter,
              filteredBoundaryTerritories: [],
              filteredBoundaries: [],
              showAll: isVisible ? TriStateRange.Full : TriStateRange.None,
            }));
          }
        },
      });
    });
  }, [activeBoundaryTerritoryGroups, boundaryFilters, boundaryGroupPerDataLayer, dataLayerInstances, dispatch, isDataLayersToolActive, t]);
};
